/**
 * Frame Selector — BucarestArt
 * El passpartú achica la imagen de verdad (no la cubre): envuelve el img
 * en un wrapper que se reduce, dejando el fondo blanco del contenedor visible.
 */

(function () {
  'use strict';

  /*
   * slice: valor border-image-slice (px en la imagen fuente recortada)
   * fw:    grosor del marco renderizado en la imagen del producto (px)
   * repeat: 'round' para texturas, 'stretch' para marcos lisos
   * Frames sin slice usan CSS border sólido (dorado aluminio).
   */
  const FRAME_STYLES = {
    'plateado':       { fw: 26, slice: 116, repeat: 'stretch', shadow: 'inset 0 0 6px rgba(0,0,0,0.15)', imgW: 1551, imgH: 1419 },
    'dorado-clasico': { fw: 28, slice: 142, repeat: 'stretch', shadow: '', imgW: 1682, imgH: 1472 },
  };

  const PPT_WIDTH = 40; /* grosor del passpartú en px */

  class FrameSelector {
    constructor(wrapper) {
      this.wrapper   = wrapper;
      this.sectionId = wrapper.dataset.section;
      this.panel     = document.getElementById('fs-panel-' + this.sectionId);
      this.inputMarco = document.getElementById('fs-input-marco-' + this.sectionId);
      this.inputPpt   = document.getElementById('fs-input-passpartu-' + this.sectionId);

      this.selectedFrame    = null;
      this.selectedFrameUrl = null;
      this.selectedPpt      = 'none';

      this.overlay           = null;
      this.mediaEl           = null;
      this.artworkWrapper    = null;
      this.origImgStyle      = '';
      this.origPaddingBottom = null;
      this._arPoll           = null;

      this._bindEvents();
      this._checkInitialState();
    }

    _bindEvents() {
      document.addEventListener('product:variant-change', (e) => {
        if (e.detail.sectionId !== this.sectionId) return;
        const withFrame = e.detail.variantTitle &&
                          e.detail.variantTitle.toLowerCase().includes('con marco');
        withFrame ? this._showPanel() : this._hidePanel();
      });

      this.wrapper.querySelectorAll('.fs-swatch').forEach(btn =>
        btn.addEventListener('click', () => this._selectFrame(btn))
      );

      this.wrapper.querySelectorAll('.fs-passpartu-btn').forEach(btn =>
        btn.addEventListener('click', () => this._selectPpt(btn))
      );
    }

    /* ── Estado inicial (página carga con variante ya seleccionada) ── */

    _checkInitialState() {
      const sel = document.getElementById('variant-selects-' + this.sectionId);
      if (!sel) return;
      const checked = sel.querySelector('input[type="radio"]:checked');
      if (checked && checked.value.toLowerCase().includes('con marco')) {
        this._showPanel();
      }
    }

    /* ── Panel ── */

    _showPanel() {
      this.panel.removeAttribute('hidden');
      this._setupDOM();
      if (!this.selectedFrame) {
        const first = this.wrapper.querySelector('.fs-swatch');
        if (first) this._selectFrame(first);
      }
    }

    _hidePanel() {
      this.panel.setAttribute('hidden', '');
      this._teardownDOM();
      this._resetSelections();
    }

    _resetSelections() {
      this.selectedFrame    = null;
      this.selectedFrameUrl = null;
      this.selectedPpt      = 'none';
      if (this.inputMarco) this.inputMarco.value = '';
      if (this.inputPpt)   this.inputPpt.value   = '';
      this.wrapper.querySelectorAll('.fs-swatch').forEach(b => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      this.wrapper.querySelectorAll('.fs-passpartu-btn').forEach((b, i) => {
        b.classList.toggle('is-selected', i === 0);
        b.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
      });
    }

    /* ── Selección de marco ── */

    _selectFrame(btn) {
      this.wrapper.querySelectorAll('.fs-swatch').forEach(b => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');
      this.selectedFrame    = btn.dataset.frame;
      this.selectedFrameUrl = btn.dataset.frameUrl || null;
      if (this.inputMarco) this.inputMarco.value = btn.dataset.frameLabel || this.selectedFrame;
      this._applyVisuals();
    }

    /* ── Selección de passpartú ── */

    _selectPpt(btn) {
      this.wrapper.querySelectorAll('.fs-passpartu-btn').forEach(b => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');
      this.selectedPpt = btn.dataset.passpartu || 'none';
      if (this.inputPpt) this.inputPpt.value = btn.dataset.passpartuLabel || '';
      this._applyVisuals();
    }

    /* ── DOM: setup / teardown ── */

    _getMediaEl() {
      const gallery = document.getElementById('MediaGallery-' + this.sectionId);
      if (!gallery) return null;
      return (
        gallery.querySelector('.product-images__slide.is-active .product-single__media-image') ||
        gallery.querySelector('.product-images__slide .product-single__media-image')
      );
    }

    _setupDOM() {
      if (this.overlay) return; // ya configurado

      const media = this._getMediaEl();
      if (!media) return;
      this.mediaEl = media;

      /* Asegurar position relative en el contenedor */
      if (getComputedStyle(media).position === 'static') {
        media.style.position = 'relative';
      }

      /* Ajustar el aspect-ratio del contenedor al de la imagen real.
         Si la imagen no terminó de cargar (carga en frío con "Con marco" pre-seleccionado),
         naturalWidth = 0 y hay que esperar el evento load. */
      this.origPaddingBottom = media.style.getPropertyValue('--padding-bottom');
      const imgEl = media.querySelector('img');
      const setAR = (ar) => {
        if (!ar || !isFinite(ar) || !this.mediaEl) return;
        const pb = (1 / ar * 100).toFixed(4) + '%';
        this.mediaEl.style.setProperty('--padding-bottom', pb);
        this.mediaEl.style.paddingBottom = pb;
        this.mediaEl.style.aspectRatio = ar.toFixed(6);
        if (this._arPoll) { clearInterval(this._arPoll); this._arPoll = null; }
      };
      if (imgEl) {
        /* 1. Atributos HTML width/height — disponibles de inmediato (Shopify siempre los incluye) */
        const attrW = parseInt(imgEl.getAttribute('width'));
        const attrH = parseInt(imgEl.getAttribute('height'));
        if (attrW > 0 && attrH > 0) {
          setAR(attrW / attrH);
        } else if (imgEl.complete && imgEl.naturalWidth > 0) {
          /* 2. Imagen ya cargada en caché */
          setAR(imgEl.naturalWidth / imgEl.naturalHeight);
        } else {
          /* 3. Fallback: esperar carga + polling por si el src cambia (lazy load JS) */
          imgEl.addEventListener('load', () => setAR(imgEl.naturalWidth / imgEl.naturalHeight), { once: true });
          this._arPoll = setInterval(() => {
            if (!this.mediaEl) { clearInterval(this._arPoll); this._arPoll = null; return; }
            if (imgEl.naturalWidth > 0) setAR(imgEl.naturalWidth / imgEl.naturalHeight);
          }, 100);
          setTimeout(() => { if (this._arPoll) { clearInterval(this._arPoll); this._arPoll = null; } }, 5000);
        }
      }

      /* Overlay de marco */
      this.overlay = document.createElement('div');
      this.overlay.className = 'fs-frame-overlay';
      this.overlay.setAttribute('aria-hidden', 'true');
      Object.assign(this.overlay.style, {
        position: 'absolute', inset: '0',
        pointerEvents: 'none', zIndex: '5',
        boxSizing: 'border-box',
      });
      media.appendChild(this.overlay);

      /* Wrapper que achica la imagen */
      const img = media.querySelector('img');
      if (img) {
        this.origImgStyle = img.getAttribute('style') || '';
        const wrap = document.createElement('div');
        wrap.className = 'fs-artwork-wrapper';
        Object.assign(wrap.style, {
          position: 'absolute',
          inset: '0',
          overflow: 'hidden',
          transition: 'inset 0.25s ease',
          zIndex: '1',
          background: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        });
        img.parentNode.insertBefore(wrap, img);
        wrap.appendChild(img);
        Object.assign(img.style, {
          position: 'relative',
          inset: 'auto',
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        });
        this.artworkWrapper = wrap;
      }
    }

    _teardownDOM() {
      if (this._arPoll) { clearInterval(this._arPoll); this._arPoll = null; }
      /* Restaurar imagen original */
      if (this.artworkWrapper) {
        const img = this.artworkWrapper.querySelector('img');
        if (img) {
          this.artworkWrapper.parentNode.insertBefore(img, this.artworkWrapper);
          img.setAttribute('style', this.origImgStyle);
        }
        this.artworkWrapper.remove();
        this.artworkWrapper = null;
      }
      if (this.overlay) {
        this.overlay.remove();
        this.overlay = null;
      }
      if (this.mediaEl) {
        this.mediaEl.style.backgroundColor = '';
        /* Restaurar el aspect-ratio original del contenedor */
        if (this.origPaddingBottom !== null) {
          if (this.origPaddingBottom) {
            this.mediaEl.style.setProperty('--padding-bottom', this.origPaddingBottom);
            this.mediaEl.style.paddingBottom = this.origPaddingBottom;
          } else {
            this.mediaEl.style.removeProperty('--padding-bottom');
            this.mediaEl.style.paddingBottom = '';
          }
          this.mediaEl.style.aspectRatio = '';
          this.origPaddingBottom = null;
        }
        this.mediaEl = null;
      }
    }

    /* ── Aplicar estilos visuales ── */

    _applyVisuals() {
      if (!this.overlay || !this.mediaEl) return;

      if (!this.selectedFrame) {
        this.overlay.style.border      = '';
        this.overlay.style.borderImage = '';
        this.overlay.style.boxShadow   = '';
        this.mediaEl.style.backgroundColor = '';
        if (this.artworkWrapper) this.artworkWrapper.style.inset = '0';
        return;
      }

      const style = FRAME_STYLES[this.selectedFrame];
      if (!style) return;

      if (this.selectedFrameUrl && style.slice) {
        /* Marco con imagen — border-image */
        this.overlay.style.borderWidth  = style.fw + 'px';
        this.overlay.style.borderStyle  = 'solid';
        this.overlay.style.borderColor  = 'transparent';
        this.overlay.style.borderImage  =
          'url("' + this.selectedFrameUrl + '") ' +
          style.slice + ' / ' + style.fw + 'px / 0 ' +
          (style.repeat || 'round');
        this.overlay.style.boxShadow    = style.shadow || '';
      } else {
        /* Marco CSS sólido (dorado aluminio) */
        this.overlay.style.border       = style.border || '';
        this.overlay.style.borderImage  = 'none';
        this.overlay.style.boxShadow    = style.shadow || '';
      }

      const withPpt = this.selectedPpt === 'blanco';

      const shadowParts = [];
      if (style.shadow) shadowParts.push(style.shadow);
      if (withPpt) shadowParts.push('inset 0 0 0 ' + PPT_WIDTH + 'px white');
      this.overlay.style.boxShadow = shadowParts.join(', ');

      this.mediaEl.style.backgroundColor = '';
      if (this.artworkWrapper) {
        this.artworkWrapper.style.inset = withPpt ? (style.fw + PPT_WIDTH) + 'px' : '0px';
      }
    }
  }

  /* ── Init ── */

  function initAll() {
    document.querySelectorAll('.fs-wrapper[data-section]').forEach(wrapper => {
      if (!wrapper._fsReady) {
        wrapper._fsReady = true;
        new FrameSelector(wrapper);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);

})();
